"use client";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { SnackbarContext, type SnackbarNotification } from "./SnackbarContext";
import SnackbarRenderer from "./SnackbarRenderer";


interface SnackbarProviderProps {
  children: ReactNode;
}

interface SnackbarState {
  open: boolean;
  message: string | SnackbarNotification;
  severity: "success" | "error" | "info";
  onUndo?: () => void;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
}) => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });

  const showSuccess = useCallback((message: string, onUndo?: () => void) => {
    setSnackbar({ open: true, message, severity: "success", onUndo });
  }, []);

  const showError = useCallback((message: string) => {
    setSnackbar({ open: true, message, severity: "error" });
  }, []);

  const showInfo = useCallback((message: string | SnackbarNotification) => {
    setSnackbar({ open: true, message, severity: "info" });
  }, []);

  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (reason && typeof reason === "object" && (reason as any).isApiError) {
        showError(reason.message);
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, [showError]);

  const handleClose = useCallback(() => {
    setSnackbar((s) => ({ ...s, open: false }));
  }, []);

  const handleUndo = () => {
    if (snackbar.onUndo) {
      snackbar.onUndo();
      handleClose();
    }
  };

  return (
    <SnackbarContext.Provider value={{ showSuccess, showError, showInfo }}>
      {children}
      <SnackbarRenderer
        snackbar={snackbar}
        onClose={handleClose}
        onUndo={handleUndo}
      />
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
